import SearchIcon from '@mui/icons-material/Search';
import { AnalyticsExportButton } from './AnalyticsHeaderSlot';
import { Autocomplete, Box, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Toolbar, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import moment from 'moment';
import React from 'react';

import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { formatTourOptionLabel } from '../components/tourOptionLabel';
import StyledFilledTextField from '../components/StyledFilledTextField';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import { REVIEW_PERIODS } from './reviewInsightsUtils';

const useStyles = makeStyles((theme) => ({
    toolbarRoot: {
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    hidden: {
        visibility: 'hidden',
    },
    grow: {
        flexGrow: 1,
    },
    pillButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '86px',
        height: '32px',
        padding: '0px, 16px, 0px, 16px',
        borderRadius: '20px',
        gap: '4px',
        color: 'black',
        backgroundColor: '#D8D8D8',
        cursor: 'pointer',
    },
    selectedPillButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '86px',
        height: '32px',
        padding: '0px, 16px, 0px, 16px',
        borderRadius: '20px',
        gap: '4px',
        backgroundColor: '#007AFF',
        color: 'white',
        cursor: 'pointer',
    },
}));

// Shortcuts for the Travel Date filter (`after`, the tour's date). Hidden while the charts are shown: there the
// Review date select picks the period, and unlabelled pills beside it read as review dates.
export const TRAVEL_DATE_PRESETS = [
    { key: 'all time', label: 'All time', width: '86px' },
    { key: 'last 30', label: 'Last 30 days', days: 30, width: '123px' },
    { key: 'last 90', label: 'Last 90 days', days: 90, width: '123px' },
    { key: 'last year', label: 'Last year', days: 365, width: '93px' },
];
export const DEFAULT_TRAVEL_DATE_PRESET = 'last 30';

// The `after` value of a preset (a UTC date, as the Travel Date picker stores it); null for "All time"
export const travelDatePresetAfter = (key) => {
    const preset = TRAVEL_DATE_PRESETS.find((p) => p.key === key);
    if (!preset?.days) {
        return null;
    }
    const date = new Date();
    date.setDate(date.getDate() - preset.days);
    return date.toISOString().split('T')[0];
};

export const ReviewListToolbar = (props) => {
    const classes = useStyles();
    const { setRecordListFilters, recordListFilters } = props;
    const isSmallScreen = !useIsWidthUp('sm');
    const [remoteTripValue, setRemoteTripValue] = React.useState(null);
    const { tourOperator } = React.useContext(ContextStore);
    const reviewRatingSystem = +(tourOperator?.config?.review_rating_system || 5);
    const ratingOptions = React.useMemo(
        () => Array.from({ length: reviewRatingSystem }, (_, index) => ({ id: index + 1, value: index + 1 })),
        [reviewRatingSystem]
    );

    const [inputValue, setInputValue] = React.useState(props.query ? props.query : '');
    // Starts at the page's query, so the report on mount changes nothing: starting at '' cleared a
    // query from the URL and set it again a second later, a list and summary fetch each time.
    const [debouncedValue, setDebouncedValue] = React.useState(inputValue);

    React.useEffect(() => {
        props.setQuery(debouncedValue);
    }, [debouncedValue]);

    React.useEffect(() => {
        const isDeletion = inputValue.length < debouncedValue.length;

        const timerId = setTimeout(() => {
            setDebouncedValue(inputValue);
        }, isDeletion ? 0 : 1000); // If deleting, apply immediately; else wait for debounce

        return () => {
            clearTimeout(timerId);
        };
    }, [inputValue]);



    const search_value = (props.query && !inputValue) ? props.query : inputValue;

    const handleRatingFilterSelect = (ev, rating_data) => {
        if (rating_data.length === 0) {
            setRecordListFilters(prev => {
                const otherFilters = prev.filter(f => f.field !== 'rating');
                return [...otherFilters, { field: 'rating', value: null }];
            });

            return;
        }

        setRecordListFilters(prev => {
            const otherFilters = prev.filter(f => f.field !== 'rating');
            let removed_labels = [...new Set(rating_data.map(item => item.id ? item.id : item))];

            return [...otherFilters, { field: 'rating', value: removed_labels }];
        });
    };

    const handleTourFilterSelect = (ev, tour_data) => {
        let tour_ids = tour_data.map((t) => t.id ? t.id : t);
        if (tour_ids.length === 0) {
            tour_ids = null;
        }
        setRecordListFilters((prev) => {
            let tour_field = prev.filter((f) => f.field === 'tour_id')[0];
            return [...prev.filter((f) => f.field !== 'tour_id'), { ...tour_field, value: tour_ids }];
        });
        setRemoteTripValue(tour_data);
    };

    // Travel Date presets (TRAVEL_DATE_PRESETS)
    const handleDateFilterSelect = (option) => {
        const date_filter = travelDatePresetAfter(option);
        setRecordListFilters((prev) => {
            let tour_field = prev.filter((f) => f.field === 'after')[0];
            return [...prev.filter((f) => f.field !== 'after'), { ...tour_field, value: date_filter }];
        });
    };

    const isThisDateFilterSelected = (option) => {
        const after = recordListFilters.filter((f) => f.field === 'after')[0]?.value;
        return option === 'all time' ? !after : after === travelDatePresetAfter(option);
    };



    const handleDateRangeChange = (dateRange) => {
        let dateStart = dateRange[0];
        let dateEnd = dateRange[1];
        if (dateStart) {
            dateStart = moment.utc(dateStart);
            dateStart = dateStart.toISOString().split('T')[0];
        }
        if (dateEnd) {
            dateEnd = moment.utc(dateEnd);
            dateEnd = dateEnd.toISOString().split('T')[0];
        }


        setRecordListFilters((prev) => {
            let dateStartField = prev.filter((f) => f.field === 'after')[0];
            let dateEndField = prev.filter((f) => f.field === 'before')[0];
            return [
                ...prev.filter((f) => f.field !== 'after' && f.field !== 'before'),
                { ...dateStartField, value: dateStart },
                { ...dateEndField, value: dateEnd },
            ];
        });
    }


    const IsoToMomentFormat = (isoDate) => {
        if (!isoDate) {
            return null;
        }
        return moment.utc(isoDate);
    }

    const handleExportCSV = async () => {
        await props.onExportList();
    }


   return (
        <Toolbar disableGutters={true} className={classes.toolbarRoot}>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={8} container spacing={2} alignItems={'center'}>
                    <Grid item xs={12} sm={3}>
                        <StyledFilledTextField
                            label="Search"
                            size="small"
                            onChange={(ev) => setInputValue(ev.target.value)}
                            value={search_value}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton aria-label="do search">
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            multiple
                            value={recordListFilters.filter(rlf => rlf.field === 'rating')[0]?.value || []}
                            options={ratingOptions.filter(option => !recordListFilters.filter(rlf => rlf.field === 'rating')[0].value?.includes(option.value))}
                            getOptionLabel={(option) => option.value ? option.value : option}
                            onChange={handleRatingFilterSelect}
                            isOptionEqualToValue={(option, value) => String(option.id) === value}
                            renderInput={(params) => <TextField {...params} variant="filled" label="Rating" size="small" />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DateRangePicker
                                slots={{ field: SingleInputDateRangeField }}
                                name="allowedRange"
                                variant="filled"
                                onChange={handleDateRangeChange}
                                format="MMMM D, YYYY"
                                todayLabel="today"
                                showTodayButton
                                timezone='utc'
                                autoOk
                                label={'Travel Date'}
                                value={[IsoToMomentFormat(recordListFilters.filter(rlf => rlf.field === 'after')[0].value), IsoToMomentFormat(recordListFilters.filter(rlf => rlf.field === 'before')[0].value)]}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        variant: 'filled',
                                        fullWidth: true
                                    },
                                    actionBar: {
                                        actions: ['clear', 'accept'],
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <RemoteAutocomplete
                            filterSelected
                            InputProps={{ required: true }}
                            resource="tour"
                            label="Tour"
                            multiple
                            variant="filled"
                            size="small"
                            apiParams={{ fields: 'id,name,is_bespoke,start_date,end_date' }}
                            onChange={handleTourFilterSelect}
                            value={remoteTripValue ? remoteTripValue : recordListFilters.filter(rlf => rlf.field === 'tour_id')[0]?.value ? recordListFilters.filter(rlf => rlf.field === 'tour_id')[0]?.value : []}
                            getOptionLabel={(opt) => formatTourOptionLabel(opt)}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} container pt={isSmallScreen ? 2 : 0} pb={0} justifyContent={isSmallScreen ? 'space-between' : "flex-end"} alignItems="center" gap={1}>
                    {props.showPeriod && (
                        // CP-5220: the review date (the Date column), for the table and the charts above it
                        <FormControl variant="filled" size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="review-period-label">Review date</InputLabel>
                            <Select labelId="review-period-label" value={props.period} onChange={(ev) => props.setPeriod(ev.target.value)}>
                                {REVIEW_PERIODS.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                    {!props.showPeriod && TRAVEL_DATE_PRESETS.map((preset) => (
                        <Box
                            key={preset.key}
                            className={isThisDateFilterSelected(preset.key) ? classes.selectedPillButton : classes.pillButton}
                            onClick={() => handleDateFilterSelect(preset.key)}
                            style={{ width: preset.width }}
                        >
                            <Typography fontSize={16}>{preset.label}</Typography>
                        </Box>
                    ))}
                    <AnalyticsExportButton
                        isSmallScreen={isSmallScreen}
                        onClick={handleExportCSV}
                        tooltip="Export list as CSV"
                        anchorStyle={isSmallScreen ? {
                            position: 'absolute',
                            right: 0,
                            top: -99,
                            zIndex: 1000,
                        } : {
                            position: 'fixed',
                            right: 10,
                            top: 10,
                            zIndex: 1000,
                        }}
                    />
                </Grid>
            </Grid>
        </Toolbar>
    );
};

export default ReviewListToolbar;
